import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Copy, Globe2, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '../../App';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import InfoTip from '../InfoTip';

const statusStyle = {
  verified: 'bg-emerald-100 text-emerald-800',
  pending: 'bg-amber-100 text-amber-800',
  failed: 'bg-rose-100 text-rose-800',
};

const failureText = {
  dns_txt_record_not_found: 'The TXT record was not found yet. DNS changes can take a while to appear.',
  challenge_expired: 'This check expired. Add the domain again for a new record.',
};

const copy = async (value) => {
  try { await navigator.clipboard.writeText(value); toast.success('Copied'); } catch (_) { toast.error('Copy failed'); }
};

// Verified domains let the Tenant Admin limit invitations to the organisation's own addresses.
const DomainSettings = () => {
  const [domains, setDomains] = useState([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/organisation/domains');
      setDomains(data.domains || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load domains');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (key, action, success) => {
    setBusy(key);
    try {
      const result = await action();
      if (success) toast[result === false ? 'error' : 'success'](typeof success === 'function' ? success(result) : success);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong');
    } finally {
      setBusy('');
    }
  };

  const add = (event) => {
    event.preventDefault();
    if (!draft.trim()) return;
    run('add', async () => { await axiosInstance.post('/organisation/domains', { domain: draft.trim() }); setDraft(''); }, 'Domain added. Add the TXT record, then check it.');
  };
  const verify = (domain) => run(`verify-${domain}`, async () => {
    const { data } = await axiosInstance.post(`/organisation/domains/${encodeURIComponent(domain)}/verify`);
    return data.verified;
  }, (verified) => (verified ? `${domain} is verified` : `${domain} is not verified yet`));
  const remove = (domain) => run(`remove-${domain}`, () => axiosInstance.delete(`/organisation/domains/${encodeURIComponent(domain)}`), `${domain} removed`);

  return (
    <Card className="p-5" data-testid="organisation-domains">
      <h3 className="flex items-center gap-2 text-base font-semibold text-slate-900"><Globe2 size={18} aria-hidden="true" />Verified domains
        <InfoTip label="About verified domains">Prove your organisation owns an email domain by adding a DNS TXT record. Once verified you can limit invitations to addresses on that domain.</InfoTip>
      </h3>
      <form onSubmit={add} className="mt-3 flex flex-wrap gap-2">
        <Input value={draft} onChange={(event) => setDraft(event.target.value)} placeholder="example.com" className="max-w-xs" aria-label="Domain" data-testid="domain-input" />
        <Button type="submit" disabled={busy === 'add' || !draft.trim()}>Add domain</Button>
      </form>
      {domains.length === 0 ? <p className="mt-4 text-sm text-slate-500">No domains yet.</p> : (
        <ul className="mt-4 space-y-3">
          {domains.map((item) => (
            <li key={item.domain} className="rounded-lg border border-slate-200 p-3" data-testid={`domain-${item.domain}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="flex items-center gap-2 text-sm font-semibold text-slate-900">{item.status === 'verified' && <CheckCircle2 size={15} className="text-emerald-600" aria-hidden="true" />}{item.domain}</span>
                <span className="flex items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${statusStyle[item.status] || 'bg-slate-100 text-slate-700'}`}>{item.status}</span>
                  {item.status !== 'verified' && <>
                    <Button size="sm" variant="outline" disabled={busy === `verify-${item.domain}`} onClick={() => verify(item.domain)}><RefreshCw size={13} className="mr-1" />Check now</Button>
                    <Button size="sm" variant="outline" className="text-rose-700" disabled={busy === `remove-${item.domain}`} onClick={() => remove(item.domain)} aria-label={`Remove ${item.domain}`}><Trash2 size={13} /></Button>
                  </>}
                </span>
              </div>
              {item.status !== 'verified' && item.challenge && (
                <div className="mt-2 space-y-1 rounded-md bg-slate-50 p-2 text-xs text-slate-700">
                  <p>Add this TXT record at your DNS provider:</p>
                  {[['Name', item.challenge.name], ['Value', item.challenge.value]].map(([label, value]) => (
                    <p key={label} className="flex items-center gap-2"><span className="w-12 text-slate-500">{label}</span><code className="min-w-0 flex-1 truncate">{value}</code><button type="button" onClick={() => copy(value)} aria-label={`Copy ${label}`} className="text-slate-500 hover:text-slate-800"><Copy size={13} /></button></p>
                  ))}
                </div>
              )}
              {item.failure_reason && <p className="mt-2 text-xs text-rose-700">{failureText[item.failure_reason] || 'Verification failed.'}</p>}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};

export default DomainSettings;
